import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Checkbox } from "@/components/ui/Field";
import { formatCurrency, formatQuantityWithUnit } from "@/lib/documentCalc";
import type { DokumentPosition } from "@/lib/dokumente";
import {
  POSITIONSART_LABELS,
  isOptionalOrAlternativPosition,
  normalizePositionTyp,
  positionDisplayTitle,
  positionTotals,
  resolvePositionsart,
  syncLeistungEinzelpreis,
  toApiPosition,
  toEditorPosition,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";
import { writeGdMeta, readGdMeta } from "@/pages/dokumente/gdPositionMeta";

export interface AngebotPositionAuswahlDialogProps {
  open: boolean;
  positions: DokumentPosition[];
  currency?: string;
  confirming?: boolean;
  zIndexClass?: string;
  onClose: () => void;
  onConfirm: (selectedPositionIds: number[]) => void;
}

function selectionKey(row: EditorPosition): string {
  if (row.id != null) return `id:${row.id}`;
  return `local:${row.localId}`;
}

function positionIdFromKey(key: string, rows: EditorPosition[]): number | null {
  if (!key.startsWith("id:")) return null;
  const id = Number(key.slice(3));
  if (!Number.isFinite(id)) return null;
  return rows.some((row) => row.id === id) ? id : null;
}

/** Positionen für AB: Standard + ausgewählte Optional/Alternativ, ohne Markierung. */
export function buildAuftragsbestaetigungPositionen(
  sourcePositions: DokumentPosition[],
  acceptedOptionalOrAlternativIds: number[],
): DokumentPosition[] {
  const accepted = new Set(acceptedOptionalOrAlternativIds);
  const editorRows = sourcePositions.map((row, index) => toEditorPosition(row, index));
  const kept = editorRows.filter((row) => {
    const art = resolvePositionsart(row);
    if (!art) return true;
    return row.id != null && accepted.has(row.id);
  });

  return kept.map((row, index) => {
    const meta = readGdMeta(row.kalkulation_json);
    const stripped = writeGdMeta(row.kalkulation_json, {
      ...meta,
      positionsart: undefined,
    });
    const withoutArt: EditorPosition = {
      ...row,
      kalkulation_json: stripped as EditorPosition["kalkulation_json"],
      quelle_position_id: row.id ?? row.quelle_position_id ?? null,
    };
    const api = toApiPosition(withoutArt, index, kept);
    return {
      ...api,
      id: undefined,
      quelle_position_id: row.id ?? row.quelle_position_id ?? null,
    };
  });
}

export function AngebotPositionAuswahlDialog({
  open,
  positions,
  currency = "EUR",
  confirming = false,
  zIndexClass = "z-[60]",
  onClose,
  onConfirm,
}: AngebotPositionAuswahlDialogProps) {
  const specialRows = useMemo(() => {
    return positions
      .map((row, index) => toEditorPosition(row, index))
      .filter((row) => isOptionalOrAlternativPosition(row));
  }, [positions]);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) return;
    // Standard: optional/alternativ zunächst nicht übernommen – Nutzer muss aktiv wählen.
    setSelectedKeys(new Set());
  }, [open, specialRows]);

  const optionalRows = specialRows.filter((row) => resolvePositionsart(row) === "optional");
  const alternativRows = specialRows.filter((row) => resolvePositionsart(row) === "alternativ");

  function toggleKey(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleConfirm() {
    const ids = [...selectedKeys]
      .map((key) => positionIdFromKey(key, specialRows))
      .filter((id): id is number => id != null);
    onConfirm(ids);
  }

  function renderGroup(title: string, rows: EditorPosition[]) {
    if (rows.length === 0) return null;
    return (
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-label">{title}</h3>
        <ul className="space-y-2">
          {rows.map((row) => {
            const key = selectionKey(row);
            const typ = normalizePositionTyp(row.position_typ);
            const priced = typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
            const totals = positionTotals(priced);
            const art = resolvePositionsart(row);
            const checked = selectedKeys.has(key);
            return (
              <li
                key={key}
                className="rounded-ios border border-separator bg-fill-quaternary/15 px-3 py-2.5"
              >
                <Checkbox
                  id={`ab-pos-wahl-${key}`}
                  checked={checked}
                  onChange={() => toggleKey(key)}
                  label={positionDisplayTitle(row)}
                  description={
                    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-label-secondary">
                      {art ? (
                        <span className="font-medium text-label">{POSITIONSART_LABELS[art]}</span>
                      ) : null}
                      <span>{formatQuantityWithUnit(priced.menge, priced.einheit)}</span>
                      <span className="tabular-nums">
                        {formatCurrency(totals.zeilen_netto, currency)} netto
                      </span>
                      {row.id == null ? (
                        <span className="text-status-orange">
                          Position noch nicht gespeichert – bitte Angebot speichern.
                        </span>
                      ) : null}
                    </span>
                  }
                  disabled={row.id == null}
                />
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  const unsaved = specialRows.some((row) => row.id == null);

  return (
    <Dialog
      open={open}
      onClose={confirming ? () => undefined : onClose}
      title="Optionale und alternative Positionen"
      description="Welche dieser Positionen hat der Kunde beauftragt? Nur ausgewählte Positionen werden in die Auftragsbestätigung übernommen – ohne Optional-/Alternativ-Markierung."
      size="2xl"
      fullScreenMobile
      zIndexClass={zIndexClass}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <DialogCancelButton onClose={onClose} disabled={confirming} label="Abbrechen" />
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={unsaved || confirming}
            loading={confirming}
          >
            Auswahl übernehmen und AB erstellen
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {unsaved ? (
          <Alert variant="warning">
            Einige markierte Positionen haben noch keine gespeicherte ID. Bitte das Angebot zuerst
            speichern und die Umwandlung erneut starten.
          </Alert>
        ) : null}

        <Alert variant="info">
          Nicht ausgewählte optionale oder alternative Positionen werden nicht in die
          Auftragsbestätigung übernommen. Standardpositionen werden immer übernommen.
        </Alert>

        {renderGroup("Optionale Positionen", optionalRows)}
        {renderGroup("Alternative Positionen", alternativRows)}

        {specialRows.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine optionalen oder alternativen Positionen.</p>
        ) : null}
      </div>
    </Dialog>
  );
}
